<template>
    <div class="cs-plot-config">
        <div class="cs-config-section">
            <div class="cs-section-header">
                <span>Output Lines</span>
                <button class="cs-add-btn" @click="addOutput">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                </button>
            </div>

            <div
                v-for="(output, i) in localConfig.outputs"
                :key="i"
                class="cs-output-card"
            >
                <div class="cs-output-row">
                    <input
                        class="cs-output-key"
                        v-model="output.key"
                        placeholder="key"
                        @input="emitUpdate"
                    />
                    <input
                        class="cs-output-label"
                        v-model="output.label"
                        placeholder="Label"
                        @input="emitUpdate"
                    />
                    <input
                        type="color"
                        v-model="output.color"
                        class="cs-color-input"
                        @input="emitUpdate"
                    />
                    <select v-model="output.style" class="cs-style-select" @change="emitUpdate">
                        <option value="line">Line</option>
                        <option value="dashed">Dashed</option>
                        <option value="histogram">Histogram</option>
                        <option value="dots">Dots</option>
                    </select>
                    <input
                        type="number"
                        v-model.number="output.width"
                        class="cs-width-input"
                        min="0"
                        max="5"
                        step="0.5"
                        placeholder="Width"
                        @input="emitUpdate"
                    />
                    <button class="cs-opt-remove" @click="removeOutput(i)">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Bands -->
        <div class="cs-config-section">
            <div class="cs-section-header">
                <span>Horizontal Bands</span>
                <button class="cs-add-btn" @click="addBand">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                </button>
            </div>

            <div v-if="!localConfig.bands || !localConfig.bands.length" class="cs-empty-hint">
                No bands configured. Add horizontal reference lines (e.g., overbought/oversold levels).
            </div>

            <div
                v-for="(band, i) in (localConfig.bands || [])"
                :key="'band-' + i"
                class="cs-band-row"
            >
                <input
                    type="number"
                    v-model.number="band.value"
                    placeholder="Value"
                    class="cs-band-value"
                    @input="emitUpdate"
                />
                <input
                    v-model="band.label"
                    placeholder="Label"
                    class="cs-band-label"
                    @input="emitUpdate"
                />
                <input
                    type="color"
                    v-model="band.color"
                    class="cs-color-input"
                    @input="emitUpdate"
                />
                <select v-model="band.style" class="cs-style-select" @change="emitUpdate">
                    <option value="dashed">Dashed</option>
                    <option value="solid">Solid</option>
                    <option value="dotted">Dotted</option>
                </select>
                <button class="cs-opt-remove" @click="removeBand(i)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Y Range -->
        <div class="cs-config-section">
            <div class="cs-section-header">
                <span>Y-Axis Range (optional)</span>
            </div>
            <div class="cs-range-row">
                <div>
                    <label>Min</label>
                    <input
                        type="number"
                        :value="localConfig.range ? localConfig.range[0] : ''"
                        @input="onRangeChange(0, $event)"
                        placeholder="Auto"
                    />
                </div>
                <div>
                    <label>Max</label>
                    <input
                        type="number"
                        :value="localConfig.range ? localConfig.range[1] : ''"
                        @input="onRangeChange(1, $event)"
                        placeholder="Auto"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PlotConfig',
    props: {
        config: { type: Object, default: () => ({ outputs: [] }) },
    },
    data() {
        return {
            localConfig: JSON.parse(JSON.stringify(this.config)),
        };
    },
    watch: {
        config: {
            handler(val) {
                this.localConfig = JSON.parse(JSON.stringify(val));
            },
            deep: true,
        },
    },
    methods: {
        addOutput() {
            if (!this.localConfig.outputs) this.localConfig.outputs = [];
            this.localConfig.outputs.push({
                key: 'output' + (this.localConfig.outputs.length + 1),
                label: 'Output ' + (this.localConfig.outputs.length + 1),
                color: '#2962ff',
                width: 1.5,
                style: 'line',
            });
            this.emitUpdate();
        },

        removeOutput(index) {
            this.localConfig.outputs.splice(index, 1);
            this.emitUpdate();
        },

        addBand() {
            if (!this.localConfig.bands) {
                this.$set(this.localConfig, 'bands', []);
            }
            this.localConfig.bands.push({
                value: 50,
                label: 'Level',
                color: '#aaaaaa',
                style: 'dashed',
            });
            this.emitUpdate();
        },

        removeBand(index) {
            this.localConfig.bands.splice(index, 1);
            this.emitUpdate();
        },

        onRangeChange(idx, event) {
            const val = event.target.value;
            if (!this.localConfig.range) {
                this.$set(this.localConfig, 'range', [null, null]);
            }
            this.localConfig.range[idx] = val !== '' ? Number(val) : null;

            if (this.localConfig.range[0] === null && this.localConfig.range[1] === null) {
                this.$delete(this.localConfig, 'range');
            }
            this.emitUpdate();
        },

        emitUpdate() {
            this.$emit('update', JSON.parse(JSON.stringify(this.localConfig)));
        },
    },
};
</script>

<style scoped>
.cs-plot-config {
    padding: 16px;
}

.cs-config-section {
    margin-bottom: 24px;
}

.cs-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--tc-text-secondary, #787b86);
}

.cs-add-btn {
    background: none;
    border: 1px solid var(--tc-border-primary, #e0e3eb);
    border-radius: 6px;
    color: var(--tc-text-secondary, #787b86);
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    transition: all 0.15s;
}
.cs-add-btn:hover {
    border-color: var(--tc-accent, #2962ff);
    color: var(--tc-accent, #2962ff);
}

.cs-output-card {
    border: 1px solid var(--tc-border-primary, #e0e3eb);
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 8px;
}

.cs-output-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.cs-output-key {
    width: 70px;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-output-label {
    flex: 1;
    font-size: 13px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-color-input {
    width: 28px;
    height: 28px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    padding: 2px;
    cursor: pointer;
    background: var(--tc-input-bg, #fff);
}

.cs-style-select {
    width: 90px;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
    cursor: pointer;
}

.cs-width-input {
    width: 50px;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-opt-remove {
    background: none;
    border: none;
    color: var(--tc-text-tertiary, #9ca3af);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
}
.cs-opt-remove:hover {
    color: var(--tc-red, #ef4444);
    background: var(--tc-red-bg, rgba(239,68,68,0.1));
}

.cs-empty-hint {
    font-size: 12px;
    color: var(--tc-text-tertiary, #9ca3af);
    padding: 8px 0;
}

.cs-band-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.cs-band-value {
    width: 70px;
    font-size: 13px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-band-label {
    flex: 1;
    font-size: 13px;
    padding: 4px 6px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    outline: none;
}

.cs-range-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}
.cs-range-row div {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cs-range-row label {
    font-size: 12px;
    color: var(--tc-text-secondary, #787b86);
}
.cs-range-row input {
    padding: 6px 8px;
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 4px;
    background: var(--tc-input-bg, #fff);
    color: var(--tc-text-primary, #131722);
    font-size: 13px;
    outline: none;
}
</style>
